<template>
    <div>
        <template v-if="isPopupVisible">
            <popupAnotacao @onClose="closePopup" :reportNode="popup.reportNode" :annotation="popup.annotation"
                :xPosition="popup.position.x" :yPosition="popup.position.y">
            </popupAnotacao>
        </template>

        <div class="popup_anotacao_opener animate-fade-in" v-if="isOpenerVisible" @click="openPopup"
            @mouseover="onMouseOverOpener" @mouseleave="onMouseLeaveOpener" :style="{
                top: opener.position.y + 'px',
                left: opener.position.x + 'px',
            }">
            <EditAnotacaoSvgIcon class="icon" />
            <span class="text">Editar</span>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import { mapGetters } from 'vuex';
import { evtBus } from '@/main';
import { gConsts } from '@/globals/gConsts';
import { utils } from '@/globals/utils';
import { anotacaoUtils } from '../lib/AnotacaoUtils';

import popupAnotacao from './popup/popupAnotacao.vue';
import EditAnotacaoSvgIcon from '@/assets/icons/edit-anotacao.svg';

import { ReportNode, LabelAnnotation } from '@/features/report/models';

export default {
    name: 'popupAnotacoesHandler',
    components: {
        popupAnotacao,
        EditAnotacaoSvgIcon,
    },
    props: {
    },

    data() {
        return {
            opener: {
                annotationId: null,
                labelNodeId: null,
                position: {
                    x: 0,
                    y: 0,
                },
                hideTimeout: null,
            },
            popup: {
                position: {
                    x: 0,
                    y: 0,
                },
                /**
                 * ReportNode que contém a annotation referida
                 * @type {ReportNode}
                 */
                reportNode: null,
                annotation: null,
            },
        }
    },

    computed: {
        ...mapGetters([
            'GET_reportNodes',
            'GET_jobTools'
        ]),

        isPopupVisible() {
            return this.popup.reportNode && this.popup.annotation;
        },

        isOpenerVisible() {
            if (this.isPopupVisible)
                return false;
            return this.opener.annotationId && this.opener.labelNodeId;
        },
    },

    methods: {

        /**
         * Decides where to place the popup relative to an annotation.
         *
         * Normally the popup is positioned just outside `locationsRect`, on the
         * side closest to `toolRect`. If `locationsRect` is invalid (empty, i.e.
         * { x: 0, y: 0, width: 0, height: 0 }), it falls back to positioning the
         * popup around `toolRect` itself, using the same screen-space logic.
         */
        calculatePopupPosition(locationsRect, toolRect) {
            const isRectInvalid = (rect) =>
                !rect || (rect.x === 0 && rect.y === 0 && rect.width === 0 && rect.height === 0);

            const targetRect = isRectInvalid(locationsRect) ? toolRect : locationsRect;
            return this.placePopupAroundRect(targetRect, toolRect);
        },

        /**
         * Positions a 350x350 popup just outside `targetRect`, choosing the side
         * closest to `referenceRect`. Sides are tried in order of proximity; the
         * first one where the popup fully fits on screen wins. If none fit, the
         * closest side is used as a fallback.
         *
         * The popup is always kept outside `targetRect` on the placement axis;
         * only the cross axis is clamped so it never re-enters the rect.
         */
        placePopupAroundRect(targetRect, referenceRect) {
            const POPUP_WIDTH = 530;
            const POPUP_HEIGHT = 350;
            const GAP = 10;
            const MARGIN = {
                top: 16,
                bottom: 100,
                left: 16,
                right: 16,
            };

            const rect = {
                left: targetRect.x,
                top: targetRect.y,
                right: targetRect.x + targetRect.width,
                bottom: targetRect.y + targetRect.height,
            };

            const referenceCenter = {
                x: referenceRect.x + referenceRect.width / 2,
                y: referenceRect.y + referenceRect.height / 2,
            };

            const clampX = (x, side) => Math.max(MARGIN[side], Math.min(x, window.innerWidth - POPUP_WIDTH - MARGIN[side]));
            const clampY = (y, side) => Math.max(MARGIN[side], Math.min(y, window.innerHeight - POPUP_HEIGHT - MARGIN[side]));

            const sides = [
                {
                    name: 'right',
                    distance: Math.abs(referenceCenter.x - rect.right),
                    build: () => ({ x: rect.right + GAP, y: clampY(rect.top, 'right') }),
                    fits: (pos) => pos.x + POPUP_WIDTH + MARGIN.right <= window.innerWidth,
                },
                {
                    name: 'left',
                    distance: Math.abs(referenceCenter.x - rect.left),
                    build: () => ({ x: rect.left - GAP - POPUP_WIDTH, y: clampY(rect.top, 'left') }),
                    fits: (pos) => pos.x >= MARGIN.left,
                },
                {
                    name: 'bottom',
                    distance: Math.abs(referenceCenter.y - rect.bottom),
                    build: () => ({ x: clampX(rect.left, 'bottom'), y: rect.bottom + GAP }),
                    fits: (pos) => pos.y + POPUP_HEIGHT + MARGIN.bottom <= window.innerHeight,
                },
                {
                    name: 'top',
                    distance: Math.abs(referenceCenter.y - rect.top),
                    build: () => ({ x: clampX(rect.left, 'top'), y: rect.top - GAP - POPUP_HEIGHT }),
                    fits: (pos) => pos.y >= MARGIN.top,
                },
            ];

            // Closest side to the reference rect first.
            sides.sort((a, b) => a.distance - b.distance);

            for (const side of sides) {
                const pos = side.build();
                if (side.fits(pos)) {
                    return pos;
                }
            }

            // Nothing fits on screen: fall back to the closest side.
            return sides[0].build();
        },

        openPopup() {
            if (!this.isOpenerVisible)
                return;

            // console.log('openPopup', JSON.stringify(this.opener, null, 2));
            const reportNode = this.GET_reportNodes.find(reportNode => reportNode.id === this.opener.labelNodeId);
            if (!reportNode || !reportNode.annotation.type)
                return;
            let referredAnnotation = reportNode.annotation.list.find(annotation => annotation.id === this.opener.annotationId);
            if (!referredAnnotation) {
                const associationType = anotacaoUtils.getAssociationTypeByAnnotationType(reportNode.annotation.type);
                if (associationType == gConsts.node.report.annotation.association.GROUP && reportNode.annotation.list.length > 0) {
                    referredAnnotation = _.cloneDeep(reportNode.annotation.list[0]);
                    referredAnnotation.setToolId(this.opener.annotationId);
                } else {
                    referredAnnotation = new LabelAnnotation(reportNode.annotation.type, this.opener.annotationId);
                }
            }

            // console.log('openPopup', reportNode, referredAnnotation, this.opener.position);
            this.popup.reportNode = reportNode;
            this.popup.annotation = referredAnnotation;

            // Busco os elementos associados a anotacao para se basear na posicao do popup
            let toolAnnotationElement = utils.findToolElementById(document, referredAnnotation.toolId);
            toolAnnotationElement = toolAnnotationElement.querySelector('#g_tool_bodyAndHandles') || toolAnnotationElement;
            const toolAnnotationBoundingRect = toolAnnotationElement.getBoundingClientRect();

            let locationToolElements = anotacaoUtils.getLocationToolElementsFromAnnotation(this.GET_jobTools, referredAnnotation);
            locationToolElements = locationToolElements.map(element => element.querySelector('#g_tool_bodyAndHandles') || element);
            const locationsBoundingRect = utils.getCombinedBoundingRect([toolAnnotationElement, ...locationToolElements]);
            // console.log('locationToolElements', locationToolElements);
            // console.log('locationsBoundingRect', locationsBoundingRect);
            this.popup.position = this.calculatePopupPosition(locationsBoundingRect, toolAnnotationBoundingRect);

            evtBus.$emit('evt_popupAnotacao_onOpen', {
                annotation: this.popup.annotation,
            });
            this.hideOpener();
        },

        closePopup() {
            this.popup.reportNode = null;
            this.popup.annotation = null;
            this.popup.position = {
                x: 0,
                y: 0,
            };

            evtBus.$emit('evt_popupAnotacao_onClose');
        },

        showOpener(payload) {
            // console.log("showOpener: ", payload);
            this.opener.position = payload.position;
            this.opener.annotationId = payload.annotationId;
            this.opener.labelNodeId = payload.labelNodeId;
        },

        delayedHideOpener() {
            clearTimeout(this.opener.hideTimeout);
            this.opener.hideTimeout = setTimeout(() => {
                this.hideOpener();
            }, 1000);
        },

        hideOpener() {
            this.opener.position = {
                x: 0,
                y: 0,
            };
            this.opener.annotationId = null;
            this.opener.labelNodeId = null;
        },

        onMouseOverOpener() {
            clearTimeout(this.opener.hideTimeout);
        },

        onMouseLeaveOpener() {
            this.delayedHideOpener();
        },
    },

    mounted() {
        evtBus.$on('evt_popupAnotacao_opener_show', (payload) => {
            this.showOpener(payload);
        });

        evtBus.$on('evt_popupAnotacao_opener_hide', () => {
            this.delayedHideOpener();
        });

        evtBus.$on('evt_popupAnotacao_open', () => {
            this.openPopup();
        });

        evtBus.$on('evt_popupAnotacao_close', () => {
            this.closePopup();
        });
    },
}
</script>

<style scoped>
.popup_anotacao_opener {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    position: absolute;
    background-color: var(--color-primary);
    color: var(--color-white);
    padding: var(--spacing-sm) var(--spacing-md);
    border-radius: var(--radius-md);
    transition: opacity 0.2s ease-in-out;
    z-index: 1000;
    cursor: pointer;
}

.popup_anotacao_opener:hover {
    opacity: 0.8;
}

.popup_anotacao_opener .icon {
    width: 15px;
    height: 15px;
}

.popup_anotacao_opener .text {
    font-size: var(--text-xs);
    font-weight: 600;
    text-align: center;
}
</style>